package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Progression en dix jetons (idée DragonBox reprise par la feuille de route v2) :
 * une quantité qu'on voit se remplir, pas un pourcentage. Chaque jeton vaut 10 %.
 *
 * Avec [onChange], toucher le jeton k règle la valeur à k × 10 ; toucher le dernier jeton
 * rempli le vide. Sans [onChange], simple affichage.
 */
/**
 * Barre continue, sans jetons : pour les avancements trop fins pour dix jetons.
 * Un parcours de six cents heures avance d'une micro de dix minutes ; un jeton
 * en vaudrait soixante, le premier n'apparaîtrait qu'après des semaines.
 */
@Composable
fun TeddFineProgress(
    fraction: Float,
    modifier: Modifier = Modifier,
    accent: Color? = null,
    height: Dp = 6.dp,
) {
    val shape = RoundedCornerShape(height / 2)
    Box(modifier = modifier.fillMaxWidth().height(height).clip(shape).background(TeddTokens.SurfaceSunken, shape)) {
        val f = fraction.coerceIn(0f, 1f)
        if (f > 0f) {
            Box(
                modifier = Modifier
                    // Une avancée existe toujours à l'écran, même infime : deux pixels au moins.
                    .fillMaxWidth(f)
                    .widthIn(min = 2.dp)
                    .height(height)
                    .clip(shape)
                    .background(accent ?: TeddTokens.LimeStrong, shape),
            )
        }
    }
}

@Composable
fun TeddTokenProgress(
    value: Int,
    modifier: Modifier = Modifier,
    onChange: ((Int) -> Unit)? = null,
    accent: Color? = null,
    height: Dp = 22.dp,
    showLabel: Boolean = true,
) {
    val filled = ((value.coerceIn(0, 100) + 5) / 10).coerceIn(0, 10)
    val fill = accent ?: TeddTokens.LimeStrong
    Row(modifier = modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(modifier = Modifier.weight(1f), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            for (k in 1..10) {
                val on = k <= filled
                val shape = RoundedCornerShape(TeddTokens.RXs)
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(height)
                        .clip(shape)
                        .background(if (on) fill else TeddTokens.SurfaceSunken, shape)
                        .then(
                            if (onChange != null) {
                                Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                    onChange(if (k == filled) (k - 1) * 10 else k * 10)
                                }
                            } else Modifier,
                        ),
                )
            }
        }
        if (showLabel) {
            Text(
                text = "$filled / 10",
                fontFamily = LocalTeddFonts.current.mono,
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = TeddTokens.Ink,
                modifier = Modifier.padding(start = 2.dp),
            )
        }
    }
}
